import { UserOut } from '@/services/noteGoApi/noteGoApiSchemas';

/**
 * Check if a user has beta access based on their subscription product name
 * @param userData - User data containing subscription information
 * @returns true if the user has beta access, false otherwise
 */
export const isBetaUser = (userData: UserOut | null | undefined, betaFeatures?: Record<string, boolean>): boolean => {
  const hasBetaFeature = betaFeatures ? Object.values(betaFeatures).some(Boolean) : false;
  return hasBetaProductName(userData) || hasBetaFeature;
};

/**
 * Check ONE beta feature: the user's plan or an admin grant includes that key, or the
 * user is on a legacy BETA-named product (all-access). Gate every individual beta
 * feature with this — isBetaUser only answers "has any beta access at all" (Beta nav
 * item and Beta page access), so using it on a feature leaks every beta feature to a
 * user granted just one.
 * @param userData - User data containing subscription information
 * @param betaFeatures - Resolved betaFeatures from useCurrentPlanFeatures (plan + extras)
 * @param feature - The beta feature key, e.g. 'noteFormats'
 */
export const hasBetaFeature = (
  userData: UserOut | null | undefined,
  betaFeatures: Record<string, boolean> | undefined,
  feature: string,
): boolean => {
  return hasBetaProductName(userData) || betaFeatures?.[feature] === true;
};

/**
 * Check if a user's subscription product name contains 'BETA' (legacy all-access check).
 * Used in page guards to allow legacy BETA plan users full access to all beta features.
 */
export const hasBetaProductName = (userData: UserOut | null | undefined): boolean => {
  return (userData?.subscription?.product_name?.toUpperCase() ?? '').includes('BETA');
};

/**
 * Check if a user has a specific product name in their subscription
 * @param userData - User data containing subscription information
 * @param productName - Product name to check for
 * @returns true if the user has the specified product, false otherwise
 */
export const hasSubscriptionProduct = (userData: UserOut | null | undefined, productName: string): boolean => {
  return (userData?.subscription?.product_name?.toUpperCase() ?? '').includes(productName.toUpperCase());
};
